/* ============================================================================
   TAC DESIGN SYSTEM - CORE JS (tac-core.js)
   Tactically Acquired - Shared JavaScript Architecture

   Replaces:
   - 14+ independent scroll listeners → 1 RAF-throttled dispatcher
   - 11+ IntersectionObservers → 1 pooled observer system
   - 6+ duplicate /cart.js fetches → 1 cached cart API
   - 3+ debounce implementations → 1 shared utility
   - 2 $()/$$(} redefinitions → 1 utility set
   - Mixed DOMContentLoaded / shopify:section:load → 1 registerSection()

   Usage:
     TAC.registerSection('my-section', {
       init: function(el, sectionId) { ... },
       destroy: function(el, sectionId) { ... }
     });
   ============================================================================ */

(function() {
  'use strict';

  /* Bail if already loaded (prevent double-init from async/deferred race) */
  if (window.TAC && window.TAC._version) return;

  var bootstrapTAC = window.TAC && !window.TAC._version ? window.TAC : null;
  var pendingSections = bootstrapTAC && Array.isArray(bootstrapTAC._pendingSections)
    ? bootstrapTAC._pendingSections.slice()
    : [];


  /* Debug flag - set TAC_DEBUG=true in console to enable warnings */
  var DEBUG = window.TAC_DEBUG || false;
  function warn(msg, e) { if (DEBUG) console.warn(msg, e); }

  /* ========================================================================
     UTILITIES
     ======================================================================== */

  /**
   * querySelector shorthand
   * @param {string} sel - CSS selector
   * @param {Element} [ctx] - Context element (default: document)
   * @returns {Element|null}
   */
  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  /**
   * querySelectorAll shorthand (returns real array)
   * @param {string} sel - CSS selector
   * @param {Element} [ctx] - Context element (default: document)
   * @returns {Element[]}
   */
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /**
   * getElementById shorthand
   * @param {string} id
   * @returns {Element|null}
   */
  function el(id) {
    return document.getElementById(id);
  }

  /**
   * Debounce - delays execution until wait ms after last call
   * @param {Function} fn
   * @param {number} wait - Milliseconds
   * @returns {Function}
   */
  function debounce(fn, wait) {
    var timer = null;
    return function() {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function() {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /**
   * Throttle - executes at most once per wait ms using RAF
   * @param {Function} fn
   * @param {number} [wait] - Minimum ms between calls (default: 16 ~60fps)
   * @returns {Function}
   */
  function throttle(fn, wait) {
    var last = 0;
    var timer = null;
    wait = wait || 16;
    return function() {
      var ctx = this;
      var args = arguments;
      var now = Date.now();
      var remaining = wait - (now - last);
      clearTimeout(timer);
      if (remaining <= 0) {
        last = now;
        fn.apply(ctx, args);
      } else {
        timer = setTimeout(function() {
          last = Date.now();
          fn.apply(ctx, args);
        }, remaining);
      }
    };
  }


  /* ========================================================================
     SCROLL DISPATCHER
     Single scroll listener, RAF-throttled. Sections subscribe.
     Replaces 14+ independent window scroll listeners.
     ======================================================================== */

  var scrollCallbacks = [];
  var scrollTicking = false;

  function onScrollFrame() {
    var scrollY = window.pageYOffset;
    var viewportH = window.innerHeight;
    for (var i = 0; i < scrollCallbacks.length; i++) {
      try {
        scrollCallbacks[i].fn(scrollY, viewportH);
      } catch (e) {
        /* Don't let one bad callback kill the scroll loop */
        warn('[TAC] Scroll callback error:', e);
      }
    }
    scrollTicking = false;
  }

  function onScroll() {
    if (!scrollTicking) {
      scrollTicking = true;
      requestAnimationFrame(onScrollFrame);
    }
  }

  /**
   * Subscribe to scroll events (RAF-throttled, single listener)
   * @param {string} id - Unique ID for this subscription (used for cleanup)
   * @param {Function} fn - Called with (scrollY, viewportHeight) on each frame
   * @returns {Function} Unsubscribe function
   */
  function onScrollSubscribe(id, fn) {
    /* Remove existing subscription with same ID (prevent duplicates) */
    scrollCallbacks = scrollCallbacks.filter(function(cb) { return cb.id !== id; });
    scrollCallbacks.push({ id: id, fn: fn });

    /* Attach global listener on first subscriber */
    if (scrollCallbacks.length === 1) {
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    return function unsubscribe() {
      scrollCallbacks = scrollCallbacks.filter(function(cb) { return cb.id !== id; });
      if (scrollCallbacks.length === 0) {
        window.removeEventListener('scroll', onScroll);
      }
    };
  }


  /* ========================================================================
     INTERSECTION OBSERVER POOL
     Shared observers by threshold+rootMargin combo.
     Replaces 11+ separate IntersectionObserver instances.
     ======================================================================== */

  var observerPool = {};

  /**
   * Get a key for observer config deduplication
   */
  function observerKey(options) {
    var t = (options.threshold || 0).toString();
    var r = options.rootMargin || '0px';
    return t + '|' + r;
  }

  /**
   * Observe an element with a shared IntersectionObserver.
   *
   * @param {Element} element - Element to observe
   * @param {Function} callback - Called with (entry, observer) when intersecting
   * @param {Object} [options]
   * @param {number} [options.threshold=0.15] - Intersection threshold
   * @param {string} [options.rootMargin='0px 0px -50px 0px'] - Root margin
   * @param {boolean} [options.once=true] - Unobserve after first intersection
   * @returns {Function} Unobserve function
   */
  function observe(element, callback, options) {
    if (!element || typeof callback !== 'function') return function() {};

    options = options || {};
    var threshold = options.threshold !== undefined ? options.threshold : 0.15;
    var rootMargin = options.rootMargin || '0px 0px -50px 0px';
    var once = options.once !== undefined ? options.once : true;

    var key = observerKey({ threshold: threshold, rootMargin: rootMargin });

    /* Guard: if IntersectionObserver is not supported, fire callback immediately */
    if (!('IntersectionObserver' in window)) {
      try { callback({ isIntersecting: true, target: element }, null); } catch (e) {}
      return function() {};
    }

    /* Create pooled observer if none exists for this config */
    if (!observerPool[key]) {
      observerPool[key] = {
        entries: new Map(),
        observer: new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            var record = observerPool[key] && observerPool[key].entries.get(entry.target);
            if (!record) return;
            if (entry.isIntersecting) {
              try {
                record.callback(entry, observerPool[key].observer);
              } catch (e) {
                warn('[TAC] Observer callback error:', e);
              }
              if (record.once) {
                observerPool[key].observer.unobserve(entry.target);
                observerPool[key].entries.delete(entry.target);
              }
            }
          });
        }, { threshold: threshold, rootMargin: rootMargin })
      };
    }

    var pool = observerPool[key];
    pool.entries.set(element, { callback: callback, once: once });
    pool.observer.observe(element);

    /* Return cleanup function */
    return function unobserve() {
      if (pool.entries.has(element)) {
        pool.observer.unobserve(element);
        pool.entries.delete(element);
      }
    };
  }

  /**
   * Convenience: reveal elements with .tac-reveal → .is-visible
   * @param {Element|Element[]} elements
   * @param {Object} [options] - Same as observe() options
   * @returns {Function[]} Array of unobserve functions
   */
  function reveal(elements, options) {
    if (!Array.isArray(elements)) elements = [elements];
    var cleanups = [];

    elements.forEach(function(el) {
      if (!el) return;
      cleanups.push(observe(el, function() {
        el.classList.add('is-visible');
      }, options));
    });

    return cleanups;
  }

  /**
   * Convenience: staggered reveal with delay per index
   * @param {Element[]} elements
   * @param {number} [delayMs=80] - Delay between each element
   * @param {Object} [options] - Same as observe() options
   * @returns {Function[]} Array of unobserve functions
   */
  function staggerReveal(elements, delayMs, options) {
    delayMs = delayMs || 80;
    var cleanups = [];

    elements.forEach(function(el, idx) {
      if (!el) return;
      cleanups.push(observe(el, function() {
        setTimeout(function() {
          el.classList.add('is-visible');
        }, idx * delayMs);
      }, options));
    });

    return cleanups;
  }


  /* ========================================================================
     CART API
     Single cached cart interface. No duplicate fetches.
     Replaces 6+ independent /cart.js calls.
     ======================================================================== */

  var cartCache = null;
  var cartFetchPromise = null;
  var CART_SELECTORS = '[data-cart-count], .cart-count, .site-header-cart--count span';

  function dispatchCartUpdated(detail) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: detail }));
    document.dispatchEvent(new CustomEvent('tac:cart:updated', { detail: detail }));
  }

  var cart = {
    /**
     * Get current cart (cached for 2 seconds to prevent storms)
     * @param {boolean} [force=false] - Bypass cache
     * @returns {Promise<Object>} Cart object
     */
    get: function(force) {
      if (!force && cartCache && (Date.now() - cartCache._timestamp < 2000)) {
        return Promise.resolve(cartCache);
      }

      /* Coalesce concurrent requests */
      if (cartFetchPromise) return cartFetchPromise;

      cartFetchPromise = fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      })
        .then(function(r) { return r.json(); })
        .then(function(data) {
          data._timestamp = Date.now();
          cartCache = data;
          cartFetchPromise = null;
          return data;
        })
        .catch(function(err) {
          cartFetchPromise = null;
          throw err;
        });

      return cartFetchPromise;
    },

    /**
     * Add item(s) to cart
     * @param {Array<{id: number, quantity: number}>} items
     * @returns {Promise<Object>} Response from /cart/add.js
     */
    add: function(items) {
      cartCache = null; /* Invalidate cache */

      /* Accept FormData (from quick-add forms) or JSON item arrays */
      var isFormData = typeof FormData !== 'undefined' && items instanceof FormData;
      var fetchOpts = {
        method: 'POST',
        credentials: 'same-origin'
      };
      if (isFormData) {
        fetchOpts.headers = { 'Accept': 'application/json' };
        fetchOpts.body = items;
      } else {
        fetchOpts.headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
        fetchOpts.body = JSON.stringify({ items: Array.isArray(items) ? items : [items] });
      }

      return fetch('/cart/add.js', fetchOpts)
        .then(function(r) {
          if (!r.ok) throw new Error('Add to cart failed (' + r.status + ')');
          return r.json();
        })
        .then(function(data) {
          /* Auto-update cart count UI + dispatch event */
          cart.updateCount();
          return data;
        });
    },

    /**
     * Change item quantity
     * @param {string} key - Line item key
     * @param {number} quantity - New quantity (0 to remove)
     * @returns {Promise<Object>}
     */
    change: function(key, quantity) {
      cartCache = null;

      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function(r) { return r.json(); })
        .then(function(data) {
          data._timestamp = Date.now();
          cartCache = data;
          cart.updateCount();
          return data;
        });
    },

    /**
     * Update all cart count elements in the DOM + dispatch event
     */
    updateCount: function() {
      cart.get(true).then(function(data) {
        var countEls = $$(CART_SELECTORS);
        for (var i = 0; i < countEls.length; i++) {
          countEls[i].textContent = data.item_count;
        }
        /* Announce to screen readers via ARIA live region */
        var liveRegion = document.getElementById('tac-cart-live');
        if (!liveRegion) {
          liveRegion = document.createElement('div');
          liveRegion.id = 'tac-cart-live';
          liveRegion.setAttribute('role', 'status');
          liveRegion.setAttribute('aria-live', 'polite');
          liveRegion.setAttribute('aria-atomic', 'true');
          liveRegion.className = 'visually-hidden';
          document.body.appendChild(liveRegion);
        }
        liveRegion.textContent = data.item_count === 1
          ? '1 item in cart'
          : data.item_count + ' items in cart';

        dispatchCartUpdated(data);
      }).catch(function() {
        /* Cart count update is non-critical - fail silently */
      });
    }
  };


  /* ========================================================================
     SECTION REGISTRY
     Unified lifecycle management for Shopify sections.
     Handles both DOMContentLoaded and shopify:section:load/unload.
     ======================================================================== */

  var sectionRegistry = {};
  var sectionInstances = {};

  /**
   * Register a section type with init/destroy hooks.
   *
   * @param {string} name - Section type name (matches data-section-type attribute)
   * @param {Object} handlers
   * @param {Function} handlers.init - Called with (sectionElement, sectionId). Return cleanup fns in an array.
   * @param {Function} [handlers.destroy] - Called with (sectionElement, sectionId) on unload.
   *
   * @example
   *   TAC.registerSection('after-action-review', {
   *     init: function(el, id) {
   *       var cleanups = TAC.reveal(TAC.$$('.aar-beat', el));
   *       return cleanups;
   *     },
   *     destroy: function(el, id) {
   *       // additional cleanup if needed
   *     }
   *   });
   */
  function registerSection(name, handlers) {
    sectionRegistry[name] = handlers;

    /* Auto-init any sections already in the DOM */
    $$('[data-section-type="' + name + '"]').forEach(function(el) {
      initSection(name, el);
    });
  }

  function initSection(name, sectionEl) {
    var handlers = sectionRegistry[name];
    var initFn = handlers && (handlers.init || handlers.onLoad);
    if (!initFn) return;

    var sectionId = sectionEl.dataset.sectionId ||
                    sectionEl.id ||
                    name + '-' + Math.random().toString(36).substr(2, 6);

    /* Prevent double-init */
    if (sectionEl.dataset.tacInit) return;
    sectionEl.dataset.tacInit = '1';

    try {
      var cleanups = initFn(sectionEl, sectionId);
      var normalizedCleanups = [];
      if (typeof cleanups === 'function') {
        normalizedCleanups = [cleanups];
      } else if (Array.isArray(cleanups)) {
        normalizedCleanups = cleanups;
      }
      sectionInstances[sectionId] = {
        name: name,
        el: sectionEl,
        cleanups: normalizedCleanups,
        handlers: handlers
      };
    } catch (e) {
      warn('[TAC] Section init error (' + name + '):', e);
    }
  }

  function destroySection(sectionId) {
    var instance = sectionInstances[sectionId];
    if (!instance) return;

    /* Run registered cleanups (unobserve fns, scroll unsubscribes, etc.) */
    if (instance.cleanups) {
      instance.cleanups.forEach(function(fn) {
        if (typeof fn === 'function') {
          try { fn(); } catch (e) {
            warn('[TAC] Cleanup error:', e);
          }
        }
      });
    }

    /* Run destroy handler if provided */
    if (instance.handlers.destroy) {
      try {
        instance.handlers.destroy(instance.el, sectionId);
      } catch (e) {
        warn('[TAC] Section destroy error (' + instance.name + '):', e);
      }
    }

    /* Remove init flag so section can re-initialize */
    if (instance.el) {
      delete instance.el.dataset.tacInit;
    }

    delete sectionInstances[sectionId];
  }


  /* ========================================================================
     SHOPIFY LIFECYCLE HOOKS
     Wire into Shopify's theme editor events.
     ======================================================================== */

  document.addEventListener('shopify:section:load', function(e) {
    var target = e.target;
    if (!target) return;

    Object.keys(sectionRegistry).forEach(function(name) {
      var el = target.querySelector('[data-section-type="' + name + '"]');
      if (el) {
        /* Destroy first if re-loading (theme editor hot reload) */
        var sid = el.dataset.sectionId || el.id;
        if (sid && sectionInstances[sid]) {
          destroySection(sid);
        }
        initSection(name, el);
      }
    });
  });

  document.addEventListener('shopify:section:unload', function(e) {
    var target = e.target;
    if (!target) return;

    Object.keys(sectionInstances).forEach(function(sid) {
      var instance = sectionInstances[sid];
      if (instance && instance.el && target.contains(instance.el)) {
        destroySection(sid);
      }
    });
  });

  document.addEventListener('shopify:section:select', function(e) {
    var target = e.target;
    if (!target) return;

    Object.keys(sectionInstances).forEach(function(sid) {
      var instance = sectionInstances[sid];
      if (instance && instance.el && target.contains(instance.el)) {
        if (instance.handlers.onSelect) {
          try { instance.handlers.onSelect(instance.el, sid); } catch (err) {
            warn('[TAC] Section select error:', err);
          }
        }
      }
    });
  });

  document.addEventListener('shopify:section:deselect', function(e) {
    var target = e.target;
    if (!target) return;

    Object.keys(sectionInstances).forEach(function(sid) {
      var instance = sectionInstances[sid];
      if (instance && instance.el && target.contains(instance.el)) {
        if (instance.handlers.onDeselect) {
          try { instance.handlers.onDeselect(instance.el, sid); } catch (err) {
            warn('[TAC] Section deselect error:', err);
          }
        }
      }
    });
  });


  /* ========================================================================
     RESIZE DISPATCHER
     Single resize listener, debounced. Sections subscribe.
     ======================================================================== */

  var resizeCallbacks = [];

  var handleResize = debounce(function() {
    var width = window.innerWidth;
    var height = window.innerHeight;
    for (var i = 0; i < resizeCallbacks.length; i++) {
      try {
        resizeCallbacks[i].fn(width, height);
      } catch (e) {
        warn('[TAC] Resize callback error:', e);
      }
    }
  }, 150);

  /**
   * Subscribe to resize events (debounced, single listener)
   * @param {string} id - Unique ID for this subscription
   * @param {Function} fn - Called with (windowWidth, windowHeight)
   * @returns {Function} Unsubscribe function
   */
  function onResizeSubscribe(id, fn) {
    resizeCallbacks = resizeCallbacks.filter(function(cb) { return cb.id !== id; });
    resizeCallbacks.push({ id: id, fn: fn });

    if (resizeCallbacks.length === 1) {
      window.addEventListener('resize', handleResize, { passive: true });
    }

    return function unsubscribe() {
      resizeCallbacks = resizeCallbacks.filter(function(cb) { return cb.id !== id; });
      if (resizeCallbacks.length === 0) {
        window.removeEventListener('resize', handleResize);
      }
    };
  }


  /* ========================================================================
     CUSTOM EVENTS
     Standardized event bus for cross-section communication.
     ======================================================================== */

  /**
   * Emit a custom event on document
   * @param {string} name - Event name (auto-prefixed with 'tac:')
   * @param {*} [detail] - Event detail data
   */
  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent('tac:' + name, { detail: detail }));
  }

  /**
   * Listen for a custom event on document
   * @param {string} name - Event name (auto-prefixed with 'tac:')
   * @param {Function} fn - Handler
   * @returns {Function} Remove listener function
   */
  function on(name, fn) {
    var handler = function(e) { fn(e.detail, e); };
    document.addEventListener('tac:' + name, handler);
    return function off() {
      document.removeEventListener('tac:' + name, handler);
    };
  }


  /* ========================================================================
     BREAKPOINT HELPERS
     ======================================================================== */

  var BREAKPOINTS = {
    sm: 600,
    md: 768,
    lg: 1024,
    xl: 1280
  };

  /**
   * Check if viewport matches breakpoint
   * @param {string} query - 'sm', 'md', 'lg', 'xl', or 'sm-down', 'md-up', etc.
   * @returns {boolean}
   */
  function breakpoint(query) {
    var w = window.innerWidth;
    var parts = query.split('-');
    var bp = BREAKPOINTS[parts[0]];
    if (!bp) return false;

    if (parts[1] === 'down') return w < bp;
    if (parts[1] === 'up') return w >= bp;
    /* Default: exact breakpoint and up */
    return w >= bp;
  }


  /* ========================================================================
     REDUCED MOTION CHECK
     ======================================================================== */

  /**
   * Check if animations should be reduced
   * Respects both CSS prefers-reduced-motion AND Shopify reduce_animations setting
   * @returns {boolean}
   */
  function prefersReducedMotion() {
    if (document.body && document.body.hasAttribute('data-reduce-animations')) return true;
    if (document.documentElement.hasAttribute('data-reduce-animations')) return true;
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }


  /* ========================================================================
     OVERLAY MANAGER
     Unified open/close/escape handling for .tac-overlay elements.
     ======================================================================== */

  var _activeOverlays = [];

  function overlayOpen(overlayEl) {
    if (!overlayEl || overlayEl.classList.contains('is-active')) return;
    overlayEl.classList.add('is-active');
    _activeOverlays.push(overlayEl);
    document.body.classList.add('tac-overlay-open');

    /* Close on backdrop click */
    var backdrop = $('[data-tac-overlay-backdrop]', overlayEl) || $('.tac-overlay__backdrop', overlayEl);
    if (backdrop) {
      backdrop._tacOverlayClick = function() { overlayClose(overlayEl); };
      backdrop.addEventListener('click', backdrop._tacOverlayClick);
    }

    /* Close buttons */
    $$('[data-tac-overlay-close]', overlayEl).forEach(function(btn) {
      btn._tacOverlayClick = function() { overlayClose(overlayEl); };
      btn.addEventListener('click', btn._tacOverlayClick);
    });

    emit('overlay:open', { overlay: overlayEl });
  }

  function overlayClose(overlayEl) {
    if (!overlayEl || !overlayEl.classList.contains('is-active')) return;
    overlayEl.classList.remove('is-active');
    _activeOverlays = _activeOverlays.filter(function(o) { return o !== overlayEl; });

    if (_activeOverlays.length === 0) {
      document.body.classList.remove('tac-overlay-open');
    }

    /* Clean up listeners */
    var backdrop = $('[data-tac-overlay-backdrop]', overlayEl) || $('.tac-overlay__backdrop', overlayEl);
    if (backdrop && backdrop._tacOverlayClick) {
      backdrop.removeEventListener('click', backdrop._tacOverlayClick);
      delete backdrop._tacOverlayClick;
    }
    $$('[data-tac-overlay-close]', overlayEl).forEach(function(btn) {
      if (btn._tacOverlayClick) {
        btn.removeEventListener('click', btn._tacOverlayClick);
        delete btn._tacOverlayClick;
      }
    });

    emit('overlay:close', { overlay: overlayEl });
  }

  function overlayToggle(overlayEl) {
    if (!overlayEl) return;
    if (overlayEl.classList.contains('is-active')) {
      overlayClose(overlayEl);
    } else {
      overlayOpen(overlayEl);
    }
  }

  /* Global Escape key handler - closes topmost overlay */
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && _activeOverlays.length > 0) {
      overlayClose(_activeOverlays[_activeOverlays.length - 1]);
    }
  });


  /* ========================================================================
     PRODUCT CARD - VARIANT ENGINE v2
     Deterministic variant resolution with full matrix validation.

     Each card stores variant data in data-variants (JSON).
     Resolution: builds a complete options array from active buttons,
     then finds the ONE variant where every option matches exactly.
     No partial matching. No ambiguity.
     ======================================================================== */

  /* ─── Color map (JS-side for runtime swatch fallback) ─── */
  var COLOR_MAP = {
    'Military Green': '#4b5320',
    'Black': '#1a1a1a',
    'Woodland Brown': '#5c4033',
    'Coyote Brown': '#8b6f47',
    'Navy': '#1b2a4a',
    'Heather Gray': '#9a9a9a',
    'OD Green': '#3d4f2f',
    'Sand': '#c2b280',
    'White': '#f5f5f0',
    'Red': '#8b1a1a',
    'Charcoal': '#36454f',
    'Dark Heather': '#4a4a4a',
    'Forest Green': '#2d4a2d',
    'Olive Drab': '#6b7c3e',
    'Tan': '#c4a86b',
    'Slate': '#4a5568'
  };

  /**
   * Parse + cache variant data from a card.
   * Returns { variants, optionNames, optionCount } or null.
   */
  function parseCardVariants(card) {
    /* Cache parsed data on the element to avoid re-parsing */
    if (card._tacParsed) return card._tacParsed;

    var raw = card.getAttribute('data-variants');
    if (!raw) return null;

    try {
      var data = JSON.parse(raw);
    } catch (e) {
      return null;
    }

    var result = {
      variants: data.variants || [],
      optionNames: data.option_names || [],
      optionCount: (data.option_names || []).length
    };

    card._tacParsed = result;
    return result;
  }

  /**
   * DETERMINISTIC VARIANT RESOLUTION
   * Builds a full options array [opt0, opt1, opt2] from active buttons,
   * then finds the exact variant where v.options[i] === options[i] for ALL i.
   * No partial matching. Returns null if no exact match.
   */
  function resolveVariant(card, parsed) {
    var options = [];

    for (var i = 0; i < parsed.optionCount; i++) {
      var active = card.querySelector('[data-option-index="' + i + '"].active');
      if (active) {
        options[i] = active.getAttribute('data-option-value');
      } else {
        /* No selection for this option - can't resolve */
        return null;
      }
    }

    /* Exact match: every option position must match */
    for (var v = 0; v < parsed.variants.length; v++) {
      var variant = parsed.variants[v];
      var match = true;
      for (var j = 0; j < parsed.optionCount; j++) {
        if (variant.options[j] !== options[j]) {
          match = false;
          break;
        }
      }
      if (match) return variant;
    }

    return null;
  }

  /**
   * FULL MATRIX VALIDATION
   * For every option button in the card, tests whether selecting it
   * (combined with all other current selections) produces a valid variant.
   * Marks buttons as is-unavailable or is-sold-out-option accordingly.
   *
   * Runs on EVERY click - ensures the entire card state is always consistent.
   */
  function validateMatrix(card, parsed) {
    /* Get current full selection array */
    var currentOptions = [];
    for (var i = 0; i < parsed.optionCount; i++) {
      var active = card.querySelector('[data-option-index="' + i + '"].active');
      currentOptions[i] = active ? active.getAttribute('data-option-value') : null;
    }

    /* Test every button */
    var buttons = card.querySelectorAll('[data-option-index]');
    for (var b = 0; b < buttons.length; b++) {
      var btn = buttons[b];
      var optIdx = parseInt(btn.getAttribute('data-option-index'), 10);
      var optVal = btn.getAttribute('data-option-value');

      /* Build test array: current selections with this button's value swapped in */
      var testOptions = [];
      for (var t = 0; t < parsed.optionCount; t++) {
        testOptions[t] = (t === optIdx) ? optVal : currentOptions[t];
      }

      /* Check if ANY variant matches this test combination */
      var found = null;
      for (var v = 0; v < parsed.variants.length; v++) {
        var variant = parsed.variants[v];
        var match = true;
        for (var j = 0; j < parsed.optionCount; j++) {
          /* Skip null slots (option not yet selected) */
          if (testOptions[j] === null) continue;
          if (variant.options[j] !== testOptions[j]) {
            match = false;
            break;
          }
        }
        if (match) { found = variant; break; }
      }

      /* Apply state */
      if (!found) {
        btn.classList.add('is-unavailable');
        btn.classList.remove('is-sold-out-option');
      } else if (!found.available) {
        btn.classList.remove('is-unavailable');
        btn.classList.add('is-sold-out-option');
      } else {
        btn.classList.remove('is-unavailable');
        btn.classList.remove('is-sold-out-option');
      }
    }
  }

  /**
   * Update card UI: variant input, button state, price, image.
   */
  function updateCardState(card, variant) {
    var input = card.querySelector('.tac-variant-input');
    var btn = card.querySelector('.tac-card__cta button, .tac-card__cta .tac-btn');
    var priceEl = card.querySelector('.tac-price--current');
    var compareEl = card.querySelector('.tac-price--old');
    var frontImg = card.querySelector('.tac-img--front');

    if (variant && variant.available) {
      if (input) input.value = variant.id;
      if (btn) { btn.disabled = false; btn.textContent = 'GET YOURS'; }
      card.classList.remove('is-sold-out');
    } else if (variant && !variant.available) {
      if (input) input.value = variant.id;
      if (btn) { btn.disabled = true; btn.textContent = 'SOLD OUT'; }
      card.classList.add('is-sold-out');
    } else {
      if (btn) { btn.disabled = true; btn.textContent = 'UNAVAILABLE'; }
      card.classList.add('is-sold-out');
    }

    /* Update price */
    if (variant && priceEl) {
      priceEl.textContent = variant.price_formatted || priceEl.textContent;
    }
    if (variant && compareEl) {
      if (variant.compare_at_price_formatted && variant.compare_at_price > variant.price) {
        compareEl.textContent = variant.compare_at_price_formatted;
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    /* Swap front image if variant has one */
    if (variant && variant.featured_image && frontImg) {
      frontImg.src = variant.featured_image;
    }

    emit('product:variant-change', {
      card: card,
      variant: variant
    });
  }

  /* ─── Delegated click: single listener for all product cards ─── */
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-option-index]');
    if (!btn) return;

    var card = btn.closest('.tac-card');
    if (!card) return;

    var parsed = parseCardVariants(card);
    if (!parsed) return;

    var idx = btn.getAttribute('data-option-index');

    /* Swap active within same option group */
    var siblings = card.querySelectorAll('[data-option-index="' + idx + '"]');
    for (var i = 0; i < siblings.length; i++) siblings[i].classList.remove('active');
    btn.classList.add('active');

    /* Resolve exact variant */
    var variant = resolveVariant(card, parsed);

    /* Update card state + full matrix validation */
    updateCardState(card, variant);
    validateMatrix(card, parsed);
  });

  /* ─── Variant image preloading (on first hover, not page load) ─── */
  document.addEventListener('mouseover', function(e) {
    var card = e.target.closest('.tac-card--product');
    if (!card || card._tacPreloaded) return;
    card._tacPreloaded = true;

    var parsed = parseCardVariants(card);
    if (!parsed) return;

    var loaded = {};
    for (var i = 0; i < parsed.variants.length; i++) {
      var src = parsed.variants[i].featured_image;
      if (src && !loaded[src]) {
        (new Image()).src = src;
        loaded[src] = true;
      }
    }
  }, { passive: true });

  /* ─── Runtime color swatch helper ─── */
  function getSwatchColor(colorName) {
    return COLOR_MAP[colorName] || '#888';
  }


  /* ========================================================================
     PRODUCT CARD - LAZY INIT
     Cards in large collections (200+) are initialized only when they
     scroll into view. Uses pooled IntersectionObserver.
     ======================================================================== */

  /**
   * Initialize a product card: set smart defaults and run first matrix pass.
   * Called once per card, either immediately (small grids) or on intersection.
   */
  function initProductCard(card) {
    if (card._tacInited) return;
    card._tacInited = true;

    var parsed = parseCardVariants(card);
    if (!parsed || parsed.variants.length === 0) return;

    /* ─── Smart default selection ───
       Strategy: find the first AVAILABLE variant, not just the first variant.
       This prevents cards from loading in a "SOLD OUT" state when other
       sizes/colors are in stock. */
    var defaultVariant = null;
    for (var i = 0; i < parsed.variants.length; i++) {
      if (parsed.variants[i].available) {
        defaultVariant = parsed.variants[i];
        break;
      }
    }
    /* If everything is sold out, fall back to first variant */
    if (!defaultVariant) defaultVariant = parsed.variants[0];

    /* Set active buttons to match the default variant's options */
    for (var optIdx = 0; optIdx < parsed.optionCount; optIdx++) {
      var targetValue = defaultVariant.options[optIdx];
      var buttons = card.querySelectorAll('[data-option-index="' + optIdx + '"]');

      /* Clear any Liquid-set active states */
      for (var b = 0; b < buttons.length; b++) buttons[b].classList.remove('active');

      /* Activate the matching button */
      for (var b = 0; b < buttons.length; b++) {
        if (buttons[b].getAttribute('data-option-value') === targetValue) {
          buttons[b].classList.add('active');
          break;
        }
      }
    }

    /* Apply swatch images from data attributes (metafield support) */
    var colorSwatches = card.querySelectorAll('.tac-option-color[data-swatch-image]');
    for (var s = 0; s < colorSwatches.length; s++) {
      var imgSrc = colorSwatches[s].getAttribute('data-swatch-image');
      if (imgSrc) {
        colorSwatches[s].style.backgroundImage = 'url(' + imgSrc + ')';
        colorSwatches[s].style.backgroundSize = 'cover';
        colorSwatches[s].style.backgroundColor = 'transparent';
      }
    }

    /* Set initial state */
    var variant = resolveVariant(card, parsed);

    /* If resolve failed (no active buttons rendered for some option indexes),
       fall back to the default variant we already found.
       This handles products with no Color/Size options (flags, patches, etc). */
    if (!variant) variant = defaultVariant;

    updateCardState(card, variant);
    validateMatrix(card, parsed);
  }

  /**
   * Boot product cards.
   * < 50 cards: init all immediately.
   * >= 50 cards: lazy-init on scroll into view.
   */
  function bootProductCards() {
    var cards = document.querySelectorAll('.tac-card--product[data-variants]');
    if (!cards.length) return;

    if (cards.length < 50) {
      /* Small collection - init all now */
      for (var i = 0; i < cards.length; i++) initProductCard(cards[i]);
    } else {
      /* Large collection - lazy init via IntersectionObserver */
      if ('IntersectionObserver' in window) {
        var cardObserver = new IntersectionObserver(function(entries) {
          for (var e = 0; e < entries.length; e++) {
            if (entries[e].isIntersecting) {
              initProductCard(entries[e].target);
              cardObserver.unobserve(entries[e].target);
            }
          }
        }, { rootMargin: '200px 0px' });

        for (var i = 0; i < cards.length; i++) cardObserver.observe(cards[i]);
      } else {
        /* No IO support - init all */
        for (var i = 0; i < cards.length; i++) initProductCard(cards[i]);
      }
    }
  }

  /* Boot on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootProductCards);
  } else {
    bootProductCards();
  }

  /* Re-boot on Shopify section events (theme editor, AJAX pagination) */
  document.addEventListener('shopify:section:load', function() {
    bootProductCards();
  });


  /* ========================================================================
     PRODUCT GALLERY ARROWS - DIRECT BUTTON BINDING
     Empire's zoom handler on [data-gallery-viewer] eats bubbled clicks,
     so we bind directly to each arrow button to fire first.
     Desktop: no Flickity - toggle figures via dataset/aria-hidden.
     Mobile: Flickity active - use flkty.next()/previous().
     ======================================================================== */

  function galleryArrowHandler(e) {
    e.preventDefault();
    e.stopPropagation();

    var btn = e.currentTarget;
    var dir = btn.getAttribute('data-product-gallery-overlay-nav');
    var gallery = btn.closest('[data-product-gallery]');
    if (!gallery) return;

    var viewer = gallery.querySelector('[data-gallery-viewer]');
    if (!viewer) return;

    var figures = viewer.querySelectorAll('[data-gallery-figure]');
    if (!figures.length) return;

    var currentIndex = 0;
    for (var i = 0; i < figures.length; i++) {
      if (figures[i].dataset.gallerySelected === 'true') {
        currentIndex = i;
        break;
      }
    }

    var nextIndex = dir === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex < 0) nextIndex = figures.length - 1;
    if (nextIndex >= figures.length) nextIndex = 0;

    /* --- Mobile: Flickity controls the gallery --- */
    if (viewer.classList.contains('flickity-enabled') && window.Flickity) {
      var flkty = window.Flickity.data(viewer);
      if (flkty) {
        flkty.select(nextIndex, false, true);
        return;
      }
    }

    /* --- Primary fallback: drive the same thumbnail path Empire uses --- */
    var targetThumb = gallery.querySelector('[data-gallery-thumbnail][data-gallery-index="' + nextIndex + '"]');
    if (targetThumb) {
      targetThumb.click();
      return;
    }

    /* --- Final fallback: mirror Empire's _selectMediaByIndex --- */
    figures[currentIndex].dataset.gallerySelected = 'false';
    figures[currentIndex].setAttribute('aria-hidden', 'true');
    figures[nextIndex].dataset.gallerySelected = 'true';
    figures[nextIndex].setAttribute('aria-hidden', 'false');

    /* Sync thumbnails */
    var thumbs = gallery.querySelectorAll('[data-gallery-thumbnail][data-gallery-index]');
    if (thumbs[currentIndex]) thumbs[currentIndex].dataset.gallerySelected = 'false';
    if (thumbs[nextIndex]) thumbs[nextIndex].dataset.gallerySelected = 'true';

    /* Update viewer media type for Empire's CSS */
    viewer.dataset.selectedMediaType = figures[nextIndex].dataset.mediaType || 'image';
  }

  function initGalleryArrows() {
    var buttons = $$('[data-product-gallery-overlay-nav]');
    buttons.forEach(function(btn) {
      if (btn.dataset.tacBound) return;
      btn.dataset.tacBound = '1';
      btn.addEventListener('click', galleryArrowHandler);
    });
  }

  function syncTacMobileGallery(root) {
    if (!window.matchMedia || !window.matchMedia('(max-width: 719px)').matches) return;

    var scope = root && root.querySelectorAll ? root : document;
    var tacProducts = scope.querySelectorAll('[data-tac-pdp]');

    tacProducts.forEach(function(productEl) {
      var overlayButtons = productEl.querySelectorAll('[data-product-gallery-overlay-nav], .product-gallery__overlay-nav, .flickity-prev-next-button');
      overlayButtons.forEach(function(button) {
        if (button && button.parentNode) {
          button.parentNode.removeChild(button);
        }
      });
    });
  }

  function initTacViewingCounts(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-tac-viewing-count]');
    nodes.forEach(function(node) {
      var min = parseInt(node.getAttribute('data-tac-viewing-min') || '3', 10);
      var max = parseInt(node.getAttribute('data-tac-viewing-max') || '50', 10);
      if (isNaN(min)) min = 3;
      if (isNaN(max)) max = 50;
      if (max < min) max = min;
      node.textContent = String(Math.floor(Math.random() * (max - min + 1)) + min);
    });
  }

  document.addEventListener('DOMContentLoaded', initGalleryArrows);
  document.addEventListener('shopify:section:load', function() {
    setTimeout(initGalleryArrows, 100);
  });
  document.addEventListener('DOMContentLoaded', function() {
    initTacViewingCounts(document);
    syncTacMobileGallery(document);
  });
  document.addEventListener('shopify:section:load', function(e) {
    setTimeout(function() {
      initTacViewingCounts(e && e.target ? e.target : document);
      syncTacMobileGallery(e && e.target ? e.target : document);
    }, 50);
  });

  /* ========================================================================
     PUBLIC API
     ======================================================================== */

  window.TAC = {
    _version: '1.2.0',

    /* Utilities */
    $: $,
    $$: $$,
    el: el,
    debounce: debounce,
    throttle: throttle,

    /* Observer pool */
    observe: observe,
    reveal: reveal,
    staggerReveal: staggerReveal,

    /* Scroll dispatcher */
    onScroll: onScrollSubscribe,

    /* Resize dispatcher */
    onResize: onResizeSubscribe,

    /* Cart API */
    cart: cart,

    /* Section lifecycle */
    registerSection: registerSection,

    /* Events */
    emit: emit,
    on: on,

    /* Overlay manager */
    overlay: {
      open: overlayOpen,
      close: overlayClose,
      toggle: overlayToggle
    },

    /* Product card helpers */
    COLOR_MAP: COLOR_MAP,
    getSwatchColor: getSwatchColor,

    /* Helpers */
    breakpoint: breakpoint,
    prefersReducedMotion: prefersReducedMotion,

    /* Constants */
    BREAKPOINTS: BREAKPOINTS
  };

  pendingSections.forEach(function(entry) {
    if (!entry || !entry.name || !entry.handlers) return;
    try {
      registerSection(entry.name, entry.handlers);
    } catch (e) {
      warn('[TAC] Bootstrap section registration error (' + entry.name + '):', e);
    }
  });

  /* ========================================================================
     TAC PDP — interactive layer
     Loadout bundle, NEXT BATCH timer, LIVE inventory bar, mobile sticky bar
     variant sync, Size Intel recommendation form.
     Boots on DOMContentLoaded; idempotent.
     ======================================================================== */

  function groupThousands(digits, sep) {
    return String(digits).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  function formatMoney(cents) {
    var fmt = (window.Theme && window.Theme.moneyFormat) || '${{amount}}';
    var n = Math.max(0, Math.round(cents)) / 100;
    var s = n.toFixed(2);
    var whole = s.split('.')[0];
    var dec = s.split('.')[1];
    var noDec = String(Math.round(n));
    return fmt
      .replace('{{amount}}', groupThousands(whole, ',') + '.' + dec)
      .replace('{{amount_no_decimals}}', groupThousands(noDec, ','))
      .replace('{{amount_with_comma_separator}}', groupThousands(whole, '.') + ',' + dec)
      .replace('{{amount_no_decimals_with_comma_separator}}', groupThousands(noDec, '.'));
  }

  /* ─── Cart summary sync ───
     Empire's StaticCart patches every [data-cart-total] with the discounted
     total after an Ajax quantity change but never re-renders the summary
     rows. It dispatches `cartcount:update` on window with the /cart/change.js
     cart JSON as detail; use that to keep the pre-discount subtotal and the
     savings amount in step with the total. */
  function syncCartSummary(cartData) {
    if (!cartData || typeof cartData.original_total_price !== 'number') return;
    var subtotals = document.querySelectorAll('[data-cart-subtotal]');
    var savings = document.querySelectorAll('[data-cart-savings]');
    if (!subtotals.length && !savings.length) return;
    var discount = cartData.total_discount || 0;
    var subtotalText = formatMoney(cartData.original_total_price);
    var savingsText = formatMoney(discount);
    Array.prototype.forEach.call(subtotals, function(node) {
      node.textContent = subtotalText;
    });
    Array.prototype.forEach.call(savings, function(node) {
      node.textContent = savingsText;
      var row = node.closest ? node.closest('[data-cart-savings-row]') : null;
      if (row) row.style.display = discount > 0 ? '' : 'none';
    });
  }

  window.addEventListener('cartcount:update', function(e) {
    syncCartSummary(e && e.detail);
  });

  /* ─── Loadout bundle ─── */
  function initLoadout(root) {
    var loadout = (root || document).querySelector('[data-tac-loadout]');
    if (!loadout || loadout._tacLoadoutInit) return;
    loadout._tacLoadoutInit = true;

    var d2 = parseInt(loadout.getAttribute('data-d2'), 10) || 0;
    var d3 = parseInt(loadout.getAttribute('data-d3'), 10) || 0;
    var cards = $$('.tac-loadout__card', loadout);
    var subtotalEl = $('[data-tac-loadout-subtotal]', loadout);
    var totalEls = $$('[data-tac-loadout-total]', loadout);
    var countEl = $('[data-tac-loadout-count]', loadout);
    var hintEl = $('[data-tac-loadout-hint]', loadout);
    var progEl = $('[data-tac-loadout-progress]', loadout);
    var atcBtn = $('[data-tac-loadout-atc]', loadout);

    function recompute() {
      var picked = 0, subtotal = 0, ids = [];
      cards.forEach(function(card) {
        var input = $('[data-tac-loadout-item]', card);
        if (!input) return;
        if (input.checked) {
          picked++;
          subtotal += parseInt(input.getAttribute('data-price'), 10) || 0;
          var vid = input.getAttribute('data-id');
          if (vid) ids.push(vid);
          card.classList.add('is-selected');
        } else {
          card.classList.remove('is-selected');
        }
        var check = $('.tac-loadout__check', card);
        if (check) check.textContent = input.checked ? '\u2713' : '';
      });
      var discount = 0;
      if (picked >= 3) discount = d3;
      else if (picked >= 2) discount = d2;
      var total = Math.round(subtotal * (100 - discount) / 100);
      if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);
      totalEls.forEach(function(t) { t.textContent = formatMoney(total); });
      if (countEl) countEl.textContent = String(picked);
      if (progEl) progEl.style.width = Math.min(100, picked * 33.34) + '%';
      if (hintEl) {
        if (picked === 0) hintEl.textContent = 'PICK 1 FOR -' + d2 + '%';
        else if (picked === 1) hintEl.textContent = 'PICK 1 MORE FOR -' + d2 + '%';
        else if (picked === 2) hintEl.textContent = 'PICK 1 MORE FOR -' + d3 + '%';
        else hintEl.textContent = 'MAX SAVINGS UNLOCKED';
      }
      if (atcBtn) {
        atcBtn.disabled = picked === 0;
        atcBtn.dataset.ids = ids.join(',');
      }
      loadout.setAttribute('data-tac-loadout-picked', picked);
    }

    cards.forEach(function(card) {
      card.addEventListener('click', function(e) {
        if (card.classList.contains('tac-loadout__card--current')) return;
        var input = $('[data-tac-loadout-item]', card);
        if (!input) return;
        if (e.target.tagName !== 'INPUT') {
          input.checked = !input.checked;
        }
        recompute();
      });
    });

    if (atcBtn) {
      atcBtn.addEventListener('click', function() {
        var ids = (atcBtn.dataset.ids || '').split(',').filter(Boolean);
        if (!ids.length) return;
        var items = ids.map(function(id) { return { id: parseInt(id, 10), quantity: 1 }; });
        atcBtn.disabled = true;
        atcBtn.classList.add('is-loading');
        cart.add(items).then(function() {
          atcBtn.classList.remove('is-loading');
          atcBtn.disabled = false;
          window.location.href = '/cart';
        }).catch(function() {
          atcBtn.classList.remove('is-loading');
          atcBtn.disabled = false;
        });
      });
    }

    recompute();
  }

  /* ─── NEXT BATCH timer + shipping timer countdown ─── */
  function initTimers(root) {
    var timers = $$('[data-tac-next-batch], [data-tac-shipping-timer]', root || document);
    timers.forEach(function(t) {
      if (t._tacTimerInit) return;
      t._tacTimerInit = true;
      var cutoffH = parseInt(t.getAttribute('data-tac-cutoff-hour'), 10);
      if (isNaN(cutoffH)) cutoffH = 14;
      var valEl = t.querySelector('[data-tac-next-batch-val], [data-tac-shipping-timer-val]');
      if (!valEl) return;
      function tick() {
        var now = new Date();
        var cutoff = new Date(now);
        cutoff.setHours(cutoffH, 0, 0, 0);
        if (cutoff <= now) cutoff.setDate(cutoff.getDate() + 1);
        var diff = Math.max(0, Math.floor((cutoff - now) / 1000));
        var h = Math.floor(diff / 3600);
        var m = Math.floor((diff % 3600) / 60);
        var s = diff % 60;
        function pad(n) { return n < 10 ? '0' + n : '' + n; }
        valEl.textContent = pad(h) + ':' + pad(m) + ':' + pad(s);
      }
      tick();
      setInterval(tick, 1000);
    });
  }

  /* ─── Mobile sticky bar + LIVE inventory variant sync ─── */
  function initVariantSync() {
    var pdp = $('[data-tac-pdp]');
    if (!pdp || pdp._tacVariantSyncInit) return;
    pdp._tacVariantSyncInit = true;

    function readSelectedVariant() {
      // Prefer the form select; fall back to data-variant-id on select buttons
      var form = $('form[data-product-form]', pdp) || $('form[action*="/cart/add"]', pdp);
      if (!form) return null;
      var idField = form.querySelector('[name="id"]');
      if (!idField) return null;
      var vid = idField.value;
      var data = $('[data-section-data]', pdp);
      if (!data) return null;
      try {
        var parsed = JSON.parse(data.textContent || data.innerHTML);
        var product = parsed.product;
        if (!product || !product.variants) return null;
        for (var i = 0; i < product.variants.length; i++) {
          if (String(product.variants[i].id) === String(vid)) return product.variants[i];
        }
      } catch (e) { return null; }
      return null;
    }

    function applyVariant(v) {
      if (!v) return;

      // Mobile sticky bar
      var bar = $('[data-tac-mobile-bar]');
      if (bar) {
        var priceEl = bar.querySelector('[data-tac-mobile-price]');
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        var metaEl = bar.querySelector('[data-tac-mobile-meta]');
        if (metaEl) {
          var bits = [];
          (v.options || []).forEach(function(o) { if (o) bits.push(String(o).toUpperCase()); });
          var label = bits.join(' · ');
          if (v.inventory_management && v.inventory_quantity > 0) {
            label += ' · ' + v.inventory_quantity + ' LEFT';
          }
          metaEl.innerHTML = label ? '<span class="tac-accent">▲</span> ' + label : '';
        }
      }

      // LIVE inventory bar
      var live = $('[data-tac-live-inventory]');
      if (live) {
        var qtyEl = live.querySelector('[data-tac-live-qty]');
        var fillEl = live.querySelector('[data-tac-live-fill]');
        var qty = v.inventory_quantity != null ? v.inventory_quantity : 99;
        if (!v.inventory_management) qty = 99;
        if (qtyEl) qtyEl.textContent = String(Math.max(0, qty));
        if (fillEl) {
          var pct = qty > 50 ? 80 : qty > 20 ? 50 : qty > 5 ? 25 : qty > 0 ? 10 : 0;
          fillEl.style.width = pct + '%';
        }
        var colorEl = live.querySelector('[data-tac-live-color]');
        var sizeEl = live.querySelector('[data-tac-live-size]');
        if (colorEl || sizeEl) {
          (v.options || []).forEach(function(o, idx) {
            var lower = (o || '').toString().toLowerCase();
            // Heuristic: first option == color usually; size pattern xs/s/m/l/xl/2xl/3xl
            if (sizeEl && /^(xs|s|m|l|xl|\dxl)$/i.test(lower)) sizeEl.textContent = o.toUpperCase();
            else if (colorEl && idx === 0) colorEl.textContent = o.toUpperCase();
          });
        }
      }
    }

    // Initial paint
    applyVariant(readSelectedVariant());

    // Listen for variant input changes (covers Empire's variant radios)
    pdp.addEventListener('change', function(e) {
      if (e.target && (e.target.matches('[data-variant-option-value], [name="id"]'))) {
        // Defer so Empire's variant resolver runs first
        setTimeout(function() { applyVariant(readSelectedVariant()); }, 50);
      }
    });

    // Mobile ATC button click is owned by snippets/tac-pdp-extras.liquid
    // (initMobileATC). A second handler here made Empire post to
    // /cart/add.js twice per tap, so it was removed.
  }

  /* ─── Size Intel recommendation form ─── */
  function initSizeIntel() {
    var form = $('[data-tac-size-intel-form]') || $('.size-intel-form');
    if (!form || form._tacSizeIntelInit) return;
    form._tacSizeIntelInit = true;
    var btn = form.querySelector('[data-tac-size-intel-run], .size-intel-run');
    var out = $('[data-tac-size-intel-out]') || $('.size-intel-recommendation');
    if (!btn) return;
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      var height = parseFloat((form.querySelector('[name="height"]') || {}).value) || 0;
      var weight = parseFloat((form.querySelector('[name="weight"]') || {}).value) || 0;
      var build = ((form.querySelector('[name="build"]') || {}).value || '').toLowerCase();
      var fit = ((form.querySelector('[name="fit"]') || {}).value || '').toLowerCase();
      var size = 'M';
      if (weight) {
        if (weight < 130) size = 'S';
        else if (weight < 150) size = 'S';
        else if (weight < 170) size = 'M';
        else if (weight < 190) size = 'L';
        else if (weight < 220) size = 'XL';
        else if (weight < 250) size = '2XL';
        else if (weight < 280) size = '3XL';
        else size = '4XL';
      }
      if (build === 'athletic' || build === 'slim') {
        // shift down one size for slim build if borderline
        var order = ['S','M','L','XL','2XL','3XL','4XL'];
        var idx = order.indexOf(size);
        if (build === 'slim' && idx > 0) size = order[idx - 1];
      }
      if (fit === 'loose' || fit === 'oversized') {
        var order2 = ['S','M','L','XL','2XL','3XL','4XL'];
        var idx2 = order2.indexOf(size);
        if (idx2 < order2.length - 1) size = order2[idx2 + 1];
      }
      if (out) {
        out.innerHTML =
          '<div class="tac-eb">▲ RECOMMENDATION</div>' +
          '<div class="tac-size-intel__big">' + size + '</div>' +
          '<div class="tac-size-intel__sub">Based on ' + (height || '—') + '" · ' + (weight || '—') + ' lbs · ' + (build || 'standard') + ' · ' + (fit || 'true') + ' fit.</div>';
        out.classList.add('is-active');
      }
    });
  }

  /* Boot all PDP modules */
  function bootTacPdp() {
    initLoadout();
    initTimers();
    initVariantSync();
    initSizeIntel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootTacPdp);
  } else {
    bootTacPdp();
  }
  document.addEventListener('shopify:section:load', function() {
    setTimeout(bootTacPdp, 0);
  });

})();
